import {Box, Drawer, LinearProgress, Tooltip, Typography} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {FC, useCallback, useEffect, useRef, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import type {II18nMap} from "@/i18n/en";
import {
	NotificationItemDto,
	useGetLearnerAnnouncementsNotifications,
	useMarkNotificationsRead
} from "@/app2/shared/data";
import {useNotificationDrawer} from "@/app2/shared/contexts/NotificationDrawerContext";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {IconContext} from "@buildwitt/shapeshift-identity";
import {useContext} from "react";
import {toolbarHeight} from "@/constants";
import {NotificationDrawerItem} from "./NotificationDrawerItem";

const useStyles = makeStyles<{toolbarHeight: string}>()((theme, {toolbarHeight: th}) => ({
	drawerPaper: {
		borderRadius: "16px",
		top: `calc(${th} + 15px)`,
		height: "fit-content",
		right: "15px",
		padding: 0,
		[theme.breakpoints.down("md")]: {
			left: "15px",
			right: "15px",
			width: "auto",
			maxWidth: "none"
		}
	},
	container: {
		display: "flex",
		flexDirection: "column",
		gap: "40px",
		position: "relative",
		padding: "20px",
		paddingBottom: "40px",
		maxWidth: "100vw",
		boxSizing: "border-box",
		width: "500px",
		maxHeight: `calc(100dvh - ${toolbarHeight} + 10px)`,
		overflow: "hidden",
		[theme.breakpoints.down("md")]: {
			width: "100%",
			maxWidth: "100%"
		},
		backgroundColor: "#191919"
	},
	headerContainer: {
		display: "flex",
		alignItems: "center",
		gap: "5px",
		fontSize: "50px"
	},
	titleContainer: {flexGrow: 2},
	markAllAsReadBtn: {
		border: "none",
		boxShadow: "none",
		color: theme.palette.primary.main,
		"&:hover": {backgroundColor: "#FF9E0014"},
		"& .MuiLoadingButton-loadingIndicator": {
			color: theme.palette.primary.main
		},
		padding: 0,
		height: "32px",
		borderRadius: "4px"
	},
	notificationsContainer: {
		display: "flex",
		flexDirection: "column",
		gap: "30px",
		overflowY: "auto"
	},
	closeIcon: {fill: theme.palette.primary.main},
	actionIcon: {
		width: "30px",
		height: "30px",
		transition: "transform 0.15s ease-in-out",
		cursor: "pointer",
		"&:hover": {transform: "scale(1.1)"}
	}
}));

const tKeys = {
	title: "notifications_title",
	markAllAsRead: "notifications_mark_all_read",
	clearAll: "notifications_clear_all",
	allUpToDate: "notifications_all_up_to_date",
	markedAllRead: "notifications_marked_all_read",
	closeDrawer: "notifications_close_drawer"
};

/**
 * In-app notification drawer (improve-api backed).
 * Renders when drawerOpen from NotificationDrawerContext is true.
 */
export const NotificationDrawer: FC = () => {
	const {t} = useSSTranslation();
	const {classes} = useStyles({toolbarHeight});
	const {icon} = useContext(IconContext);
	const mq = useMq();
	const {drawerOpen, closeDrawer} = useNotificationDrawer();
	const pageSize = 25;
	const [page, setPage] = useState(1);
	const [accumulated, setAccumulated] = useState<NotificationItemDto[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [refreshId, setRefreshId] = useState(0);
	const receivedPagesRef = useRef<Set<number>>(new Set());
	const nextPageRequestedRef = useRef(false);
	const lastRequestedPageRef = useRef(0);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const rafRef = useRef<number | null>(null);
	const readInSessionRef = useRef(0);
	const scrollThreshold = 300;

	const {data, isLoading} = useGetLearnerAnnouncementsNotifications(
		drawerOpen ? {page: page, pageSize: pageSize} : undefined,
		{enabled: drawerOpen, noCache: true}
	);

	const refreshList = useCallback(() => {
		if (rafRef.current !== null) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		setPage(1);
		setAccumulated([]);
		setTotalCount(0);
		setRefreshId((id) => id + 1);
		receivedPagesRef.current = new Set();
		nextPageRequestedRef.current = false;
		lastRequestedPageRef.current = 0;
		readInSessionRef.current = 0;
	}, []);

	// Reset state when drawer opens
	useEffect(() => {
		if (drawerOpen) {
			refreshList();
		}
	}, [drawerOpen]);

	// Accumulate paginated results (same pattern as HistoricNotificationAndAnnouncementDrawer).
	// refreshId in deps ensures we re-run after refreshList() even if React Query returns same data reference.
	// For page 1 we always replace accumulated so refetched data after mark-all-read repopulates the list.
	useEffect(() => {
		if (data === undefined || !drawerOpen) return;
		const responsePage = data.page;
		const items = data.data ?? [];
		if (responsePage === 1) {
			receivedPagesRef.current = new Set([1]);
			setAccumulated(items);
			setTotalCount(data.totalCount ?? 0);
		} else {
			if (receivedPagesRef.current.has(responsePage)) return;
			receivedPagesRef.current.add(responsePage);
			setAccumulated((prev) => [...prev, ...items]);
		}
		nextPageRequestedRef.current = false;
	}, [data, drawerOpen, refreshId]);

	const label = (key: keyof typeof tKeys, fallback: string) =>
		t(tKeys[key] as keyof II18nMap, fallback);

	const markAllRead = useMarkNotificationsRead({
		onSuccess: () => {
			refreshList();
		}
	});

	const onMarkAllRead = () => {
		markAllRead.mutate(undefined);
	};

	const closeDrawerLabel = label("closeDrawer", "Close notifications drawer");

	return (
		<Drawer
			anchor="right"
			open={drawerOpen}
			onClose={closeDrawer}
			PaperProps={{className: classes.drawerPaper}}
		>
			<Box className={classes.container}>
				<Box className={classes.headerContainer}>
					<Box className={classes.titleContainer}>
						<Typography variant="h3">{label("title", "Notifications")}</Typography>
					</Box>
					{mq.mobile ? (
						<>
							<LoadingButton
								variant="text"
								loading={markAllRead.isPending}
								disabled={markAllRead.isPending}
								className={classes.markAllAsReadBtn}
								onClick={onMarkAllRead}
							>
								{label("markAllAsRead", "Mark all as read")}
							</LoadingButton>
							<Tooltip title={closeDrawerLabel}>
								<Box
									component="button"
									type="button"
									onClick={closeDrawer}
									aria-label={closeDrawerLabel}
									data-testid="historic-notification-drawer-close"
									className={classes.actionIcon}
									sx={{
										border: "none",
										background: "none",
										padding: 0,
										display: "flex",
										alignItems: "center",
										alignSelf: "center",
										cursor: "pointer",
										marginLeft: "8px"
									}}
								>
									{icon("closeIcon", {
										className: classes.closeIcon
									})}
								</Box>
							</Tooltip>
						</>
					) : (
						<Tooltip title={label("clearAll", "Clear all")}>
							<LoadingButton
								variant="text"
								loading={markAllRead.isPending}
								disabled={markAllRead.isPending}
								className={classes.markAllAsReadBtn}
								onClick={onMarkAllRead}
							>
								{label("markAllAsRead", "Mark all as read")}
							</LoadingButton>
						</Tooltip>
					)}
				</Box>

				{!isLoading && accumulated.length <= 0 ? (
					<Typography>{label("markedAllRead", "Marked all notifications as read")}</Typography>
				) : (
					(() => {
						const initialLoading = page === 1 && (!data || isLoading);
						const loadingMore = page > 1 && isLoading;
						const hasMore = accumulated.length < totalCount;

						const handleScroll = () => {
							const el = scrollContainerRef.current;
							if (!el || !hasMore || isLoading || !receivedPagesRef.current.has(page)) return;
							const {scrollTop, scrollHeight, clientHeight} = el;
							if (scrollTop + clientHeight < scrollHeight - scrollThreshold) return;

							const nextPage = page + 1;
							if (nextPage <= lastRequestedPageRef.current || nextPageRequestedRef.current) return;

							// Claim the next page immediately so a second scroll in the same frame/tick cannot also pass
							lastRequestedPageRef.current = nextPage;
							nextPageRequestedRef.current = true;
							setPage((p) => p + 1);
						};

						const handleScrollThrottled = () => {
							if (rafRef.current !== null) return;
							rafRef.current = requestAnimationFrame(() => {
								rafRef.current = null;
								handleScroll();
							});
						};

						if (initialLoading) {
							return (
								<LinearProgress
									aria-label="Loading notifications"
									data-testid="notification-drawer-loading"
								/>
							);
						}

						return (
							<>
								{accumulated.length === 0 && (
									<Typography data-testid="notification-drawer-all-up-to-date">
										{label("allUpToDate", "You are all up to date!")}
									</Typography>
								)}
								<Box
									ref={scrollContainerRef}
									className={classes.notificationsContainer}
									onScroll={handleScrollThrottled}
									aria-label="Notifications list"
									data-testid="notification-drawer-list"
								>
									{accumulated.map((item) => (
										<NotificationDrawerItem
											key={`notification-${item.id}`}
											onRead={() => {}}
											notification={item}
										/>
									))}
									{loadingMore && (
										<LinearProgress
											sx={{mt: 1}}
											aria-label="Loading more notifications"
											data-testid="notification-drawer-loading-more"
										/>
									)}
								</Box>
							</>
						);
					})()
				)}

				</Box>
		</Drawer>

	);
};
